```vue
<template>
    <drag-modal :visible="visible" @cancel="close" :title="this.$store.state.dislog.editFileURL.file_name"
        :width="modalWidth" :zIndex="10003">
        <div id="container"></div>
    </drag-modal>
</template>

<script>
import { refreshUrlToken } from '@/server/api'
export default {
    name: "fileEditing",

    data() {
        return {
            instance: null,
            tokenTimer: null, // token 刷新定时器
            refreshingToken: false, // 防止重复刷新
            browserWidth: window.innerWidth
        };
    },
    computed: {
        visible() {
            return this.$store.state.dislog.fileEditing;
        },

        fileEditingInfo() {
            return this.$store.state.dislog.editFileURL;
        },
        // 弹窗宽度
        modalWidth() {
            return (this.browserWidth - 10) + 'px';
        }
    },
    watch: {
        /**
         * 监听弹窗显示状态
         */
        visible(newVal) {
            if (newVal) {
                // 打开弹窗，开始监听 token
                this.startTokenTimer();
            } else {
                // 清除 token 定时器
                this.stopTokenTimer();
                // 销毁 WebOffice
                this.destroyWebOffice();
            }
        },
        fileEditingInfo: {
            immediate: true,
            deep: true,
            handler(data) {
                if (data && data.weboffice_url) {
                    this.$nextTick(() => {
                        this.initWebOffice(
                            data.weboffice_url,
                            data.access_token
                        );
                        // 如果弹窗已经打开 文件信息发生变化后重新设置 token 定时器
                        if (this.visible) {
                            this.startTokenTimer();
                        }
                    });
                }
            }
        }
    },
    methods: {
        // 浏览器大小改变
        handleResize() {
            this.browserWidth = window.innerWidth;
        },
        initWebOffice(url, token) {//初始化 预览文件
            const container = document.querySelector("#container");
            this.instance = aliyun.config({
                mount: container,
                url: url
            });
            this.instance.setToken({
                token
            });
        },

        startTokenTimer() {//token 定时器
            // 清除之前的定时器
            this.stopTokenTimer();
            const data = this.fileEditingInfo;
            this.setTokenTimer(data.access_token_expired_at);
        },
        /**
         * 根据 token 过期时间设置定时器
         */
        setTokenTimer(expiredAt) {
            // 清除旧定时器
            this.stopTokenTimer();
            const expireTime = this.getTimestamp(expiredAt);
            const now = Date.now();
            // 距离过期还有多久
            const delay = expireTime - now;
            if (delay <= 0) {
                // 已经到期，立即刷新
                this.refreshToken();
            } else {
                // 到期后自动刷新
                this.tokenTimer = setTimeout(() => {
                    this.refreshToken();
                }, delay);
            }
        },
        /**
         * 刷新 token
         */
        async refreshToken() {
            // 弹窗已经关闭，不刷新
            if (!this.visible) {
                return;
            }
            // 防止重复请求
            if (this.refreshingToken) {
                return;
            }
            const data = this.fileEditingInfo;
            this.refreshingToken = true;
            try {
                // 调用刷新 token 接口
                const res = await refreshUrlToken({
                    access_token: data.access_token,
                    refresh_token: data.refresh_token
                });
                // console.log("refreshUrlToken 返回：", res);
                const newTokenInfo = res.data;
                const newAccessToken =
                    newTokenInfo.access_token;
                const newRefreshToken =
                    newTokenInfo.refresh_token;
                const newAccessTokenExpiredAt =
                    newTokenInfo.access_token_expired_at;
                const newRefreshTokenExpiredAt =
                    newTokenInfo.refresh_token_expired_at;
                //   更新 实例对象的 当前 token
                this.instance.setToken({
                    token: newAccessToken
                });
                //更新 Vuex里面的信息
                const newFileEditingInfo = {
                    ...data,
                    access_token: newAccessToken,
                    refresh_token:
                        newRefreshToken || data.refresh_token,
                    access_token_expired_at:
                        newAccessTokenExpiredAt,
                    refresh_token_expired_at:
                        newRefreshTokenExpiredAt ||
                        data.refresh_token_expired_at
                };

                this.$store.dispatch(
                    "setEditFileURL",
                    newFileEditingInfo
                );
                /**
                 *  根据新的过期时间，
                 *    重新设置下一次刷新
                 */
                if (newAccessTokenExpiredAt) {
                    this.setTokenTimer(
                        newAccessTokenExpiredAt
                    );
                }
            } catch (error) {
                console.error("刷新Token失败", error);

            } finally {//成功失败都会执行
                this.refreshingToken = false;
            }
        },
        /**
         * 时间转换
         * 支持：
         * 2026-09-09 18:06:00
         * 10位时间戳
         * 13位时间戳
         */
        getTimestamp(time) {
            // 时间戳
            if (!isNaN(time)) {
                const timestamp = Number(time);
                // 10位：秒
                if (String(timestamp).length === 10) {
                    return timestamp * 1000;
                }
                // 13位：毫秒
                return timestamp;
            }
            const date = new Date(//处理时间格式
                String(time).replace(/-/g, "/")
            );
            const timestamp = date.getTime();
            return isNaN(timestamp) ? 0 : timestamp;
        },
        /**
         * 关闭弹窗
         */
        close() {
            this.$store.dispatch("openkey", {
                key: "fileEditing",
                by: false
            });
        },
        /**
         * 清除 token 定时器
         */
        stopTokenTimer() {
            if (this.tokenTimer) {
                clearTimeout(this.tokenTimer);
                this.tokenTimer = null;
            }
        },
        /**
         * 销毁 实例
         */
        destroyWebOffice() {
            if (
                this.instance &&
                typeof this.instance.destroy === "function"
            ) {
                this.instance.destroy();
            }
            this.instance = null;
        }
    },
    /**
     * 组件销毁
     */
    beforeDestroy() {
        // 清除 token 定时器
        this.stopTokenTimer();
        // 销毁 WebOffice
        this.destroyWebOffice();
        window.removeEventListener('resize', this.handleResize);
    }
};
</script>

<style lang="less" scoped>
:deep(.ant-modal) {
    top: 5px !important;
    overflow: hidden !important;
    padding-bottom: 5px !important;
}

:deep(.ant-modal-body) {
    padding: 0 !important;
}

#container {
    width: 100%;
    height: 870px;
}
</style>